import styles from 'css/src/components/agent/AskUserQuestionResult.module.scss';

import React from 'react';

import {
    ask,
    isAskUserQuestionEvalMode,
    resolvePending,
} from '../ask-user-question/askUserQuestionStore';
import type { AskQuestionAnswer, FileUploadAccept, QuestionType } from '../ask-user-question/types';
import { QUESTION_TYPES } from '../ask-user-question/types';
import {
    type CustomRendererComponentProps,
    CustomRendererMode,
    type EnhancedToolResult,
    type ToolDefinition,
    type ToolExecutionContext,
} from '../types';

import { SearchVoicesTool } from './SearchVoicesTool';
import { Tool } from './Tool';

interface AskUserQuestionArgs {
    questions: {
        question: string;
        type: QuestionType;
        options?: { label: string }[];
        accept?: FileUploadAccept;
    }[];
}

/** Only the LLM-authored choice lists need options; every other kind supplies its own. */
const TYPES_REQUIRING_OPTIONS: QuestionType[] = ['radio', 'checkbox'];

/** Returns an error describing the first invalid question, or null if all are valid. */
function validateQuestions(questions: AskUserQuestionArgs['questions']): string | null {
    if (questions.length === 0) {
        return 'No questions were provided. Pass at least one question.';
    }
    for (const question of questions) {
        if (!QUESTION_TYPES.includes(question.type)) {
            return `Question "${question.question}" has an invalid type "${question.type}". Use one of: ${QUESTION_TYPES.join(', ')}.`;
        }
        if (TYPES_REQUIRING_OPTIONS.includes(question.type) && !question.options?.length) {
            return `Question "${question.question}" is type "${question.type}" but has no options. Provide options, or use "free_text" for an open answer.`;
        }
    }
    return null;
}

const ANSWER_PREFIX = 'The user answered:';
const DISMISSED_RESULT =
    'The user dismissed the questions without answering. Do not ask again unless it is essential; proceed using your best judgment or ask the user how they would like to continue.';

function formatAnswer(answer: AskQuestionAnswer): string {
    const parts: string[] = [];
    // App-driven kinds resolve to a human-readable name (voice, template, brand,
    // uploaded file), but the agent has to act on the pick — so report the id
    // when the widget has one. A cleared pick carries an empty name, which is a
    // skip rather than an answer.
    if (answer.resolved?.name) {
        parts.push(answer.resolved.id ?? answer.resolved.name);
    }
    if (answer.selectedLabels.length > 0) {
        parts.push(answer.selectedLabels.join(', '));
    }
    if (answer.customText) {
        parts.push(`custom answer: "${answer.customText}"`);
    }
    const value = parts.length > 0 ? parts.join('; ') : '(no answer)';
    return `- "${answer.question}": ${value}`;
}

const REFERENCE_PREFIX = 'Uploaded assets, ready to use:';

/**
 * Asset references for any uploads, gathered into a block after the answer list.
 * They're kept off the `- "{question}": …` lines so those stay single-line and
 * parseable for display, while the agent still gets the IDs it needs to place
 * the assets in the video.
 */
function formatReferences(answers: AskQuestionAnswer[]): string {
    const references = answers.flatMap((answer) => answer.resolved?.references ?? []);
    return references.length > 0 ? `\n\n${REFERENCE_PREFIX}\n${references.join('\n')}` : '';
}

interface ParsedAnswer {
    question: string;
    value: string;
}

/** Pulls the `- "{question}": {value}` lines back out for display. */
function parseAnswers(result: string): ParsedAnswer[] {
    if (!result.startsWith(ANSWER_PREFIX)) {
        return [];
    }
    return result
        .split('\n')
        .filter((line) => line.startsWith('- '))
        .map((line) => {
            const match = line.match(/^- "(.*?)": (.*)$/);
            if (!match) {
                return null;
            }
            return { question: match[1], value: match[2] };
        })
        .filter((answer): answer is ParsedAnswer => answer !== null);
}

export const AskUserQuestionResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
}) => {
    if (isLoading) {
        return null;
    }
    if (result === DISMISSED_RESULT) {
        return <div className={styles['dismissed']}>Dismissed without answering</div>;
    }
    const answers = parseAnswers(result);
    if (answers.length === 0) {
        return null;
    }
    return (
        <div className={styles['answers']}>
            {answers.map((answer, index) => (
                <div key={index} className={styles['answer']}>
                    <span className={styles['answer-header']}>{answer.question}</span>
                    <span className={styles['answer-value']}>{answer.value}</span>
                </div>
            ))}
        </div>
    );
};

export class AskUserQuestionTool extends Tool<AskUserQuestionArgs> {
    public static readonly toolName = 'ask_user_question';

    public override customRenderer = AskUserQuestionResultRenderer;
    public override customRendererMode = CustomRendererMode.INLINE_HIDDEN;

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: AskUserQuestionTool.toolName,
                description:
                    'Ask the user one or more questions and wait for their answer. Use this for a decision or preference you cannot reasonably infer, instead of guessing. Bundle every question into a single call rather than asking piecemeal. The user can always dismiss without answering. Do not use markdown.',
                parameters: {
                    type: 'object',
                    properties: {
                        questions: {
                            type: 'array',
                            description: 'The questions to ask the user.',
                            items: {
                                type: 'object',
                                properties: {
                                    question: {
                                        type: 'string',
                                        description:
                                            'The full question text shown to the user. Should be clear and specific. Plain text only — no markdown.',
                                    },
                                    type: {
                                        type: 'string',
                                        enum: [...QUESTION_TYPES],
                                        description:
                                            'How the question is answered. "radio" = single select, "checkbox" = multiselect, "free_text" = text answer, "aspect_ratio" = pick an aspect ratio (pass no options). "file_upload" = upload files (set "accept"). "voice" and "template" = picker for voice/template (suggested ids as options). "brand" picker for brand kit (no options). "waveform" picker for waveform style (no options).',
                                    },
                                    options: {
                                        type: 'array',
                                        description: `The choices offered, for radio and checkbox questions. Provide 2-4 distinct options. Also used by "voice" and "template" to suggest specific ones: set each label to the voice slug (from ${SearchVoicesTool.toolName}) or template id, NOT a description — an unrecognised id is dropped. Omit for every other type. An "Other" free-text field is added automatically for radio/checkbox.`,
                                        items: {
                                            type: 'object',
                                            properties: {
                                                label: {
                                                    type: 'string',
                                                    description:
                                                        'Concise text for the choice. Plain text only — no markdown.',
                                                },
                                            },
                                            required: ['label'],
                                        },
                                    },
                                    accept: {
                                        type: 'string',
                                        enum: ['audio', 'image', 'video', 'media'],
                                        description:
                                            'For "file_upload" questions: what kind of file to accept. "media" means video or image.',
                                    },
                                },
                                required: ['question', 'type'],
                            },
                        },
                    },
                    required: ['questions'],
                },
            },
        };
    }

    public override getToolDisplayName(toolCallArgs?: string): string {
        if (toolCallArgs) {
            try {
                const parsed = JSON.parse(toolCallArgs) as Partial<AskUserQuestionArgs>;
                if ((parsed.questions?.length ?? 0) > 1) {
                    return `Asking ${parsed.questions!.length} questions`;
                }
            } catch {
                // fall through to the singular name
            }
        }
        return 'Asking a question';
    }

    public get statusIcon(): string {
        return 'quiz';
    }

    public async execute(
        args: AskUserQuestionArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const abortSignal = context.abortSignal;
        if (abortSignal?.aborted) {
            return { result: 'Question cancelled before it was shown.', isSuccess: false };
        }

        const validationError = validateQuestions(args.questions);
        if (validationError) {
            return { result: `Error: ${validationError}`, isSuccess: false };
        }

        const onAbort = () => resolvePending({ status: 'dismissed' });
        abortSignal?.addEventListener('abort', onAbort);

        try {
            const result = await ask(
                args.questions.map((question) => ({
                    ...question,
                    options: question.options ?? [],
                })),
                context.conversationId,
            );

            if (result.status === 'dismissed') {
                return {
                    result: DISMISSED_RESULT,
                    isSuccess: true,
                    // In eval mode the dismissal is synthetic (no human answered),
                    // so end the turn here rather than letting the agent proceed on
                    // an assumed answer — the ask is the behavior under test. (An eval
                    // that instead ANSWERS a prompt hits the answered branch and continues.)
                    stopConversation: isAskUserQuestionEvalMode(),
                };
            }

            const formatted = result.answers.map(formatAnswer).join('\n');
            return {
                result: `${ANSWER_PREFIX}\n${formatted}${formatReferences(result.answers)}`,
                isSuccess: true,
            };
        } finally {
            abortSignal?.removeEventListener('abort', onAbort);
        }
    }
}
